import React, { useEffect, useState } from "react";
import InputField from "../shared-components/InputField";
import { classNames } from "../utils";
import DropdownCombo, {
  DropdownItem,
} from "../shared-components/DropdownCombo";
import { JobTitleItems } from "../api/JobTitlesApi";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import {
  BasicInfoData,
  REPORT_WIZARD_STEPS,
  useReportsWizard,
} from "./ReportsWizardProvider";
import {
  isWizardNameWithinDbLimit,
  sanitizeInternationalWithDashesAndSpaces,
  sanitizeName,
} from "./ReportsWizardUtil";

export interface BasicFields {
  firstName: string;
  middleName: string;
  lastName: string;
  companyUUID: string;
  jobTitle: string;
  clientReportID: string;
}

type Props = {
  onChange?: (fields: BasicFields) => void;
};

function getJobTitle(jobTitle: string) {
  return (
    JobTitleItems.find((item) => item.id === jobTitle) ||
    JobTitleItems.find((item) => item.id === "None")
  );
}

export const ReportsWizardBasic = (props: Props) => {
  const {
    companyQuery,
    getBasicInfo,
    currentStep,
    setValidStep,
    getIsDirty,
    steps,
    parentReportUUID,
    isCreatingStagingPerson,
  } = useReportsWizard();
  const isDirty = getIsDirty(REPORT_WIZARD_STEPS.BASIC_INFO);
  const [companyItems, setCompanyItems] = useState<DropdownItem[]>([]);
  const [fields, setFields] = useState({
    firstName: getBasicInfo()?.firstName || "",
    middleName: getBasicInfo()?.middleName || "",
    lastName: getBasicInfo()?.lastName || "",
    companyUUID: getBasicInfo()?.companyUUID || "",
    jobTitle: getBasicInfo()?.jobTitle || "None",
    clientReportID: getBasicInfo()?.clientReportId || "",
  });

  useEffect(() => {
    const basicInfo = steps[REPORT_WIZARD_STEPS.BASIC_INFO]
      .data as BasicInfoData;
    setFields({
      firstName: basicInfo?.firstName || "",
      middleName: basicInfo?.middleName || "",
      lastName: basicInfo?.lastName || "",
      companyUUID: basicInfo?.companyUUID || "",
      jobTitle: basicInfo?.jobTitle || "",
      clientReportID: basicInfo?.clientReportId || "",
    });
  }, [steps]);

  const [errors, setErrors] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    fullName: "",
    companyUUID: "",
    jobTitle: "",
    clientReportID: "",
  });

  useEffect(() => {
    if (companyQuery.data && companyQuery.data.length > 0) {
      const list = companyQuery.data;
      const items: DropdownItem[] = list.map((element) => {
        return { id: element.external_id, label: element.name };
      });
      setCompanyItems(items);
    }
  }, [companyQuery.data]);

  function validateFields() {
    let hasErrors = false;
    if (fields.firstName.length === 0) {
      setErrors((prev) => ({
        ...prev,
        firstName: "First name is required",
      }));
      hasErrors = true;
    } else {
      setErrors((prev) => ({
        ...prev,
        firstName: "",
      }));
    }
    if (fields.lastName.length === 0) {
      setErrors((prev) => ({
        ...prev,
        lastName: "Last name is required",
      }));
      hasErrors = true;
    } else {
      setErrors((prev) => ({
        ...prev,
        lastName: "",
      }));
    }
    if (
      !isWizardNameWithinDbLimit({
        firstName: fields.firstName,
        middleName: fields.middleName,
        lastName: fields.lastName,
      })
    ) {
      setErrors((prev) => ({
        ...prev,
        fullName: "Combined full name must not exceed 255 characters",
      }));
      hasErrors = true;
    } else {
      setErrors((prev) => ({
        ...prev,
        fullName: "",
      }));
    }
    if (fields.companyUUID.length === 0) {
      setErrors((prev) => ({
        ...prev,
        companyUUID: "Company is required",
      }));
      hasErrors = true;
    } else {
      setErrors((prev) => ({
        ...prev,
        companyUUID: "",
      }));
    }

    setValidStep(REPORT_WIZARD_STEPS.BASIC_INFO, !hasErrors);
  }

  useEffect(() => {
    validateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields.companyUUID,
    fields.firstName,
    fields.middleName,
    fields.lastName,
    isDirty,
  ]);

  function updateField(name: string, value: string) {
    setFields({ ...fields, [name]: value });
    props.onChange?.({ ...fields, [name]: value });
  }

  const isDisabled =
    currentStep !== REPORT_WIZARD_STEPS.PERSON_INFO &&
    currentStep !== REPORT_WIZARD_STEPS.CHECKTYPES;
  return (
    <>
      <div className="flex gap-6">
        <div className="flex-1 flex flex-col gap-2">
          <InputField
            handleChange={(e) => {
              updateField("firstName", sanitizeName(e.target.value));
            }}
            name={`first-name`}
            placeholder=""
            label={"First Name*"}
            classes="border-rose-600 flex-1"
            value={fields.firstName}
            labelClassName="font-thin"
            className={classNames(
              errors?.firstName && isDirty ? "border-rose-600" : "",
              "w-full"
            )}
            disabled={isDisabled}
          />
          {isDirty && errors.firstName && (
            <div className="text-red-500 text-sm">{errors.firstName}</div>
          )}
        </div>

        <InputField
          handleChange={(e) => {
            updateField("middleName", sanitizeName(e.target.value));
          }}
          name={`middle-name`}
          placeholder=""
          label={"Middle Name"}
          value={fields.middleName}
          classes={"flex-1"}
          labelClassName="font-thin"
          disabled={isDisabled}
        />
        <div className="flex-1 flex flex-col gap-2">
          <InputField
            handleChange={(e) => {
              updateField("lastName", sanitizeName(e.target.value));
            }}
            classes="flex-1"
            name={`last-name`}
            placeholder=""
            label={"Last Name *"}
            value={fields.lastName}
            labelClassName="font-thin"
            className={classNames(
              errors?.lastName && isDirty ? "border-rose-600" : "",
              "w-full"
            )}
            disabled={isDisabled}
          />
          {isDirty && errors.lastName && (
            <div className="text-red-500 text-sm">{errors.lastName}</div>
          )}
        </div>
      </div>
      {isDirty && errors.fullName && (
        <div className="text-red-500 text-sm mt-2">{errors.fullName}</div>
      )}
      <div className="flex gap-6 mt-4">
        <div className="flex-1">
          {companyQuery.isLoading ? <LoadingSpinner /> : <></>}
          {!companyQuery.isLoading && (
            <DropdownCombo
              items={companyItems}
              initial={
                fields.companyUUID
                  ? companyItems.find((item) => item.id === fields.companyUUID)
                  : undefined
              }
              disabled={
                companyQuery.isLoading ||
                isDisabled ||
                !!parentReportUUID ||
                isCreatingStagingPerson
              }
              onChange={(item) => updateField("companyUUID", item.id)}
              label={"Company*"}
              labelClassName="font-thin pb-1 "
              placeHolder={
                companyQuery.isLoading ? "Loading" : "Select Company"
              }
              buttonContainerClassName={classNames(
                errors?.companyUUID && isDirty ? "border-rose-600" : "",
                "w-full"
              )}
              name="selected-company"
              needTypeAhead={true}
            ></DropdownCombo>
          )}
          {isDirty && errors.companyUUID && (
            <div className="text-red-500 text-sm mt-2">
              {errors.companyUUID}
            </div>
          )}
        </div>
        <div className="flex-1">
          <DropdownCombo
            containerClassName=""
            items={JobTitleItems}
            placeHolder={"Select Job Title"}
            onChange={(item) => updateField("jobTitle", item.id)}
            disabled={isDisabled}
            label={"Job Title"}
            initial={
              fields.jobTitle
                ? getJobTitle(fields.jobTitle)
                : JobTitleItems.find((item) => item.id === "None")
            }
            buttonContainerClassName="w-full"
            name="selected-job-title"
            needTypeAhead={true}
            labelClassName="font-thin pb-1"
            icon={<InformationCircleIcon height={24} width={24} color="grey" />}
            tooltip="Enter the job title for the position the candidate is applying for to enhance your searchability and reporting experience over time."
          ></DropdownCombo>
        </div>
        <div className="flex-1">
          <InputField
            handleChange={(e) => {
              updateField(
                "clientReportID",
                sanitizeInternationalWithDashesAndSpaces(e.target.value)
              );
            }}
            label="Client Report ID"
            tooltip="Enter the Client Report ID that corresponds to this candidate's overall background check report. This ensures the Fama report can be easily matched and integrated with the candidate's background check report. This is best used for CRAs."
            name={"reference-id"}
            placeholder="ID number"
            labelClassName="font-thin"
            classes="w-full"
            disabled={isDisabled}
            value={fields.clientReportID}
          />
        </div>
      </div>
    </>
  );
};
